import { Box, IconButton, Stack, Typography } from "@mui/material";
import {
  Button,
  Color,
  DatePicker,
  Modal,
  RadioButton,
  SelectInput,
  SimpleInput,
} from "@axismaxlife/unified-ui-components-library";
import { CloseIcon } from "@axismaxlife/unified-ui-components-library/dist/systemIcons";
import { useEffect, useState } from "react";
import { String } from "../constants/Strings";
import { colors } from "../style/color/colors";
import { fonts } from "../style/fonts/fonts";
import { amountInWords } from "../logic/NumberToWord";
import { formatToIndianCurrency } from "../logic/CurrencyFormatter";
import { toAlphabets } from "../logic/CommonFunctions";
import { AppToast } from "./AppToast";
import { DetailSummaryCard, AddAnotherLink } from "./DetailSummaryCard";

const Modals = String.Dashboard.snapshot.modals;

const toText = (raw) =>
  raw === null || raw === undefined || raw === 0 || raw === "0" ? "" : `${raw}`;
const toDigits = (raw) => `${raw ?? ""}`.replace(/[^0-9]/g, "");

export const parseDayMonthYear = (value) => {
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const parts = `${value ?? ""}`.split(/[-/]/);
  if (parts.length !== 3) {
    if (/^\d{4}$/.test(`${value ?? ""}`.trim())) {
      const year = Number(value);
      return new Date(year, 0, 1);
    }
    return null;
  }
  const [day, month, year] = parts.map(Number);
  if (!day || !month || !year) return null;
  const date = new Date(year, month - 1, day);
  return isNaN(date.getTime()) ? null : date;
};

export const formatDayMonthYear = (date) => {
  if (!(date instanceof Date) || isNaN(date.getTime())) return "";
  const day = `${date.getDate()}`.padStart(2, "0");
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  return `${day}-${month}-${date.getFullYear()}`;
};

const SheetHeading = ({ title, onClose }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: "12px",
    }}
  >
    <Typography
      component="h2"
      sx={{
        fontSize: "20px",
        fontFamily: fonts.lato,
        fontWeight: 700,
        color: colors.PrimaryWoodSmoke,
      }}
    >
      {title}
    </Typography>
    <IconButton sx={{ p: 0, flexShrink: 0 }} onClick={onClose} aria-label="Close">
      <CloseIcon size="small" fillColor={Color.Icons.Default} />
    </IconButton>
  </Box>
);

const SheetShell = ({ isOpen, onClose, title, children }) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    device="mobile"
    variant="bottom sheet"
    showCloseIcon
    className="acr-details-modal"
  >
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <SheetHeading title={title} onClose={onClose} />
      {children}
    </Box>
  </Modal>
);

const AmountInput = ({ name, label, value, onChange, disabled }) => {
  const displayValue =
    value === 0 || value === "0" || !value
      ? ""
      : `${value}`.replace(/[^0-9]/g, "");
  return (
    <SimpleInput
      name={name}
      type="number"
      indianNumberFormat
      prefix="₹"
      width="100%"
      label={label}
      placeholder={label}
      value={displayValue}
      disabled={disabled}
      hintText={displayValue ? amountInWords(displayValue) : ""}
      onChange={(e) => onChange(toDigits(e.target.value))}
    />
  );
};

const DateField = ({ formKey, label, value, minDate, disabled, onSet }) => (
  <DatePicker
    key={formKey}
    type="simple"
    inputWidth="100%"
    width="328px"
    dateLabel={label}
    inputPlaceholder="DD/MM/YYYY"
    minDate={minDate}
    disabled={disabled}
    initialDate={value}
    onSet={(picked) => onSet(picked instanceof Date ? picked : null)}
    onClose={() => { }}
  />
);

const EMPTY_DEPOSIT = { depositName: "", totalAmount: "", maturityDate: null };

export const DepositDetailsModal = ({
  isOpen,
  onClose,
  onSave,
  initialValue,
}) => {
  const [form, setForm] = useState(EMPTY_DEPOSIT);
  const [formKey, setFormKey] = useState(0);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastKey, setToastKey] = useState(0);

  const showNameError = () => {
    setToastOpen(true);
    setToastKey((k) => k + 1);
  };

  useEffect(() => {
    if (isOpen) {
      setForm(
        initialValue
          ? {
            depositName: toText(initialValue.depositName),
            totalAmount: toText(initialValue.totalAmount),
            maturityDate: parseDayMonthYear(initialValue.maturityDate),
          }
          : EMPTY_DEPOSIT,
      );
      setFormKey((key) => key + 1);
      setToastOpen(false);
    }
  }, [isOpen]);

  const setField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const isValid = Boolean(
    `${form.depositName}`.trim() && form.totalAmount && form.maturityDate,
  );

  return (
    <SheetShell isOpen={isOpen} onClose={onClose} title={Modals.deposit.title}>
      <SimpleInput
        name="depositName"
        width="100%"
        label={Modals.deposit.name}
        placeholder={Modals.deposit.name}
        value={form.depositName}
        onChange={(e) => setField("depositName", toAlphabets(e.target.value, showNameError))}
      />
      <AmountInput
        name="depositAmount"
        label={Modals.deposit.amount}
        value={form.totalAmount}
        onChange={(value) => setField("totalAmount", value)}
      />
      <DateField
        formKey={formKey}
        label={Modals.deposit.date}
        value={form.maturityDate}
        minDate={new Date()}
        onSet={(picked) => setField("maturityDate", picked)}
      />
      <Box sx={{ mt: "8px" }}>
        <Button
          buttonType="primary"
          width="100%"
          disabled={!isValid}
          onClick={() => {
            onSave({
              depositName: `${form.depositName}`.trim(),
              totalAmount: Number(form.totalAmount),
              maturityDate: formatDayMonthYear(form.maturityDate),
            });
          }}
        >
          {Modals.deposit.save}
        </Button>
      </Box>
      <AppToast
        key={toastKey}
        open={toastOpen}
        message="Name cannot be a number"
        severity="error"
        handleClose={() => setToastOpen(false)}
      />
    </SheetShell>
  );
};

const EMPTY_POLICY = {
  policyType: "",
  coverageAmount: "",
  maturityDate: null,
  yearlyPremium: "",
};

export const PolicyDetailsModal = ({
  isOpen,
  onClose,
  onSave,
  initialValue,
}) => {
  const [form, setForm] = useState(EMPTY_POLICY);
  const [formKey, setFormKey] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setForm(
        initialValue
          ? {
            policyType: toText(initialValue.policyType),
            coverageAmount: toText(initialValue.coverageAmount),
            maturityDate: parseDayMonthYear(initialValue.maturityDate),
            yearlyPremium: toText(initialValue.yearlyPremium),
          }
          : EMPTY_POLICY,
      );
      setFormKey((key) => key + 1);
    }
  }, [isOpen]);

  const setField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const selectedType =
    Modals.policy.options.find(
      (option) => option.value === form.policyType,
    ) || null;

  const isValid = Boolean(
    form.policyType &&
    form.coverageAmount &&
    form.maturityDate &&
    form.yearlyPremium,
  );

  return (
    <SheetShell isOpen={isOpen} onClose={onClose} title={Modals.policy.title}>
      <SelectInput
        width="100%"
        label={Modals.policy.type}
        placeholder={Modals.policy.type}
        options={Modals.policy.options}
        value={selectedType}
        preventMobileKeyboard
        onChange={(option) => setField("policyType", option ? option.value : "")}
      />
      <AmountInput
        name="coverageAmount"
        label={Modals.policy.coverage}
        value={form.coverageAmount}
        onChange={(value) => setField("coverageAmount", value)}
      />
      <DateField
        formKey={formKey}
        label={Modals.policy.date}
        value={form.maturityDate}
        minDate={new Date()}
        onSet={(picked) => setField("maturityDate", picked)}
      />
      <AmountInput
        name="yearlyPremium"
        label={Modals.policy.premium}
        value={form.yearlyPremium}
        onChange={(value) => setField("yearlyPremium", value)}
      />
      <Box sx={{ mt: "8px" }}>
        <Button
          buttonType="primary"
          width="100%"
          disabled={!isValid}
          onClick={() => {
            onSave({
              policyType: form.policyType,
              coverageAmount: Number(form.coverageAmount),
              maturityDate: formatDayMonthYear(form.maturityDate),
              yearlyPremium: Number(form.yearlyPremium),
            });
          }}
        >
          {Modals.policy.save}
        </Button>
      </Box>
    </SheetShell>
  );
};

const PF_SEGMENTS = [
  { id: "PPF", label: Modals.providentFund.ppf },
  { id: "EPF", label: Modals.providentFund.epf },
];

const EMPTY_PF_FUND = { totalAmount: "", tenure: "" };

const toPfFund = (value) => {
  if (!value || typeof value !== "object") return EMPTY_PF_FUND;
  let tenureVal = toText(value.tenure);
  if (!tenureVal && value.maturityDate) {
    const match = `${value.maturityDate}`.match(/\b(19\d\d|20\d\d)\b/);
    if (match) tenureVal = match[1];
  } else if (/^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/.test(tenureVal)) {
    const parts = tenureVal.split(/[-/]/);
    tenureVal = parts[2];
  }
  return {
    totalAmount: toText(value.totalAmount),
    tenure: tenureVal,
  };
};

export const ProvidentFundDetailsModal = ({
  isOpen,
  onClose,
  onSave,
  initialValue,
  initialSegment,
}) => {
  const [segment, setSegment] = useState(initialSegment || null);
  const [funds, setFunds] = useState({ PPF: EMPTY_PF_FUND, EPF: EMPTY_PF_FUND });
  const [formKey, setFormKey] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setSegment(initialSegment || null);
      const ppfVal = Array.isArray(initialValue)
        ? initialValue.find((item) => (item.pfType || item.fundType) === "PPF")
        : initialValue?.PPF;
      const epfVal = Array.isArray(initialValue)
        ? initialValue.find((item) => (item.pfType || item.fundType) === "EPF")
        : initialValue?.EPF;

      setFunds({
        PPF: toPfFund(ppfVal),
        EPF: toPfFund(epfVal),
      });
      setFormKey((key) => key + 1);
    }
  }, [isOpen, initialSegment]);

  const active = (segment && funds[segment]) || EMPTY_PF_FUND;

  const setField = (field, value) => {
    if (!segment) return;
    setFunds((prev) => ({
      ...prev,
      [segment]: { ...prev[segment], [field]: value },
    }));
  };

  const isValid = Boolean(
    segment &&
    funds[segment]?.totalAmount &&
    Number(funds[segment]?.totalAmount) > 0 &&
    funds[segment]?.tenure
  );

  return (
    <SheetShell
      isOpen={isOpen}
      onClose={onClose}
      title={Modals.providentFund.title}
    >
      <Box>
        <Typography
          sx={{
            mb: "8px",
            fontSize: "16px",
            fontFamily: fonts.notoSans,
            fontWeight: 500,
            color: colors.NeutralDark700,
          }}
        >
          {Modals.providentFund.fundType}
        </Typography>
        <Stack
          direction="row"
          spacing="16px"
          sx={{ mt: "8px", height: "56px", width: "242px" }}
        >
          {PF_SEGMENTS.map((item) => {
            const isSelected = segment === item.id;
            return (
              <Box
                key={item.id}
                onClick={() => setSegment(item.id)}
                sx={{
                  flex: 1,
                  p: "16px",
                  cursor: "pointer",
                  borderRadius: "8px",
                  border: `1px solid ${isSelected
                    ? colors.SecondarySummerSky
                    : colors.NeutralLightN80
                    }`,
                  backgroundColor: isSelected
                    ? colors.ExtendedPalettesSummerSkySS100
                    : colors.PrimaryWhite,
                }}
              >
                <RadioButton
                  name="fundType"
                  value={item.id}
                  label={item.label}
                  checked={isSelected}
                  onChange={() => setSegment(item.id)}
                />
              </Box>
            );
          })}
        </Stack>
      </Box>
      <AmountInput
        name={`${segment || "pf"}TotalAmount`}
        label={`${Modals.providentFund.amount}`}
        value={active.totalAmount}
        disabled={!segment}
        onChange={(value) => setField("totalAmount", value)}
      />
      <DatePicker
        key={`${formKey}-${segment || "none"}`}
        type="simple"
        variant="year"
        inputWidth="100%"
        width="328px"
        dateLabel={Modals.providentFund.maturityYear}
        inputPlaceholder="YYYY"
        minDate={new Date()}
        disabled={!segment}
        initialDate={active.tenure ? new Date(Number(active.tenure), 0, 1) : null}
        onSet={(picked) =>
          setField(
            "tenure",
            picked instanceof Date ? `${picked.getFullYear()}` : "",
          )
        }
        onClose={() => { }}
      />
      <Box sx={{ mt: "8px" }}>
        <Button
          buttonType="primary"
          width="100%"
          disabled={!isValid}
          onClick={() => {
            const pfArray = [];
            if (Number(funds.EPF.totalAmount) > 0) {
              pfArray.push({
                pfType: "EPF",
                totalAmount: Number(funds.EPF.totalAmount) || 0,
                tenure: Number(funds.EPF.tenure) || 0,
              });
            }
            if (Number(funds.PPF.totalAmount) > 0) {
              pfArray.push({
                pfType: "PPF",
                totalAmount: Number(funds.PPF.totalAmount) || 0,
                tenure: Number(funds.PPF.tenure) || 0,
              });
            }
            onSave(pfArray);
            onClose();
          }}
        >
          {Modals.providentFund.save}
        </Button>
      </Box>
    </SheetShell>
  );
};

const EMPTY_NPS = { totalHolding: "", maturityDate: null };

export const NpsDetailsModal = ({ isOpen, onClose, onSave, initialValue }) => {
  const [form, setForm] = useState(EMPTY_NPS);
  const [formKey, setFormKey] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setForm(
        initialValue
          ? {
            totalHolding: toText(initialValue.totalHolding),
            maturityDate: parseDayMonthYear(initialValue.maturityDate),
          }
          : EMPTY_NPS,
      );
      setFormKey((key) => key + 1);
    }
  }, [isOpen]);

  const setField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const isValid = Boolean(form.totalHolding && form.maturityDate);

  return (
    <SheetShell isOpen={isOpen} onClose={onClose} title={Modals.nps.title}>
      <AmountInput
        name="npsTotalHolding"
        label={Modals.nps.holding}
        value={form.totalHolding}
        onChange={(value) => setField("totalHolding", value)}
      />
      <DateField
        formKey={formKey}
        label={Modals.nps.date || Modals.policy.date}
        value={form.maturityDate}
        minDate={new Date()}
        onSet={(picked) => setField("maturityDate", picked)}
      />
      <Box sx={{ mt: "8px" }}>
        <Button
          buttonType="primary"
          width="100%"
          disabled={!isValid}
          onClick={() => {
            onSave({
              totalHolding: Number(form.totalHolding),
              maturityDate: formatDayMonthYear(form.maturityDate),
            });
            onClose();
          }}
        >
          {Modals.nps.save}
        </Button>
      </Box>
    </SheetShell>
  );
};

export const SnapshotCollectionListModal = ({
  isOpen,
  onClose,
  title,
  totalLabel,
  totalAmount = 0,
  entries = [],
  addAnotherLabel,
  onAddAnother,
  onEdit,
  onDelete,
}) => {
  return (
    <SheetShell isOpen={isOpen} onClose={onClose} title={title}>
      <Box
        sx={{
          p: "16px",
          borderRadius: "12px",
          background: "linear-gradient(45deg, #0092DB 0%, #91DCFD 100%)",
        }}
      >
        <Typography
          sx={{
            fontSize: "16px",
            fontFamily: fonts.notoSans,
            color: colors.PrimaryWhite,
          }}
        >
          {totalLabel}
        </Typography>
        <Typography
          sx={{
            mt: "8px",
            fontSize: "18px",
            fontFamily: fonts.notoSans,
            fontWeight: 600,
            color: colors.PrimaryWhite,
          }}
        >
          {formatToIndianCurrency(totalAmount || 0)}
        </Typography>
        <Typography
          sx={{
            mt: "4px",
            fontSize: "13px",
            fontFamily: fonts.notoSans,
            color: colors.PrimaryWhite,
            fontWeight: 400,
          }}
        >
          {amountInWords(totalAmount || 0)}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        {entries.map((entry, index) => (
          <DetailSummaryCard
            key={index}
            title={entry.title}
            subtitle={entry.subtitle}
            rows={entry.rows}
            onEdit={() => onEdit && onEdit(index)}
            onDelete={() => onDelete && onDelete(index)}
          />
        ))}
      </Box>

      <Box sx={{ textAlign: "center", mt: "12px", pb: "16px" }}>
        <AddAnotherLink label={addAnotherLabel} onClick={onAddAnother} />
      </Box>
      <Box sx={{ mt: "8px" }}>
        <Button
          buttonType="primary"
          width="100%"
          onClick={onClose}
        >
          {String.done}
        </Button>
      </Box>
    </SheetShell>
  );
};
